import { useState, type FormEvent, type ReactNode } from 'react';
import {
  ACCESS_LABEL,
  EMP_LEVELS,
  type EmploymentLevel,
  type User,
} from '@gdec/shared';
import { api, errorMessage } from '@/api/client';
import { useAuth, useCurrentUser } from '@/auth/AuthContext';
import { useToast } from '@/components/Toast';
import { Spinner } from '@/components/Spinner';

function splitName(name: string): { first: string; last: string } {
  const [first = '', ...rest] = name.split(' ');
  return { first, last: rest.join(' ') };
}

function isEmploymentLevel(value: string): value is EmploymentLevel {
  return (EMP_LEVELS as readonly string[]).includes(value);
}

/**
 * Ported from the prototype's `renderProfileSetup` / `wireProfileSetupEvents`
 * (lines 1133–1176). Department and System Access stay read-only here: they are
 * assigned by Main Access through the Organization Chart and must never be
 * self-serviced.
 */
export function ProfileSetupPage(): ReactNode {
  const user = useCurrentUser();
  const { refresh } = useAuth();
  const { toast } = useToast();

  const defaults = splitName(user.name);
  const [first, setFirst] = useState(defaults.first);
  const [last, setLast] = useState(defaults.last);
  const [position, setPosition] = useState(user.position);
  const [level, setLevel] = useState<EmploymentLevel>(user.employmentLevel);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!first.trim() || !last.trim()) {
      setError('First and last name are required.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await api.session.completeProfile<User>({
        firstName: first.trim(),
        lastName: last.trim(),
        position: position.trim() || user.position,
        employmentLevel: level,
      });
      await refresh();
      toast('Profile saved.', 'ok');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="content">
      <form className="panel" style={{ maxWidth: 640, margin: '0 auto' }} onSubmit={(e) => void onSubmit(e)}>
        <h3>Complete Your Profile</h3>
        <p style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: -8 }}>
          Since this is your first login, please confirm your details. This information
          automatically populates your future Expense Claim Forms.
        </p>

        {error ? (
          <div className="auth-error" role="alert">
            {error}
          </div>
        ) : null}

        <div className="form-row">
          <div className="field">
            <label htmlFor="pFirst">First Name</label>
            <input id="pFirst" value={first} onChange={(e) => setFirst(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="pLast">Last Name</label>
            <input id="pLast" value={last} onChange={(e) => setLast(e.target.value)} />
          </div>
        </div>

        <div className="form-row">
          <div className="field">
            <label htmlFor="pEmpId">Employee ID</label>
            <input id="pEmpId" value={user.empId} disabled />
          </div>
          <div className="field">
            <label htmlFor="pEmail">Company Email</label>
            <input id="pEmail" value={user.email} disabled />
          </div>
        </div>

        <div className="form-row">
          <div className="field">
            <label htmlFor="pDept">Department</label>
            <input id="pDept" value={user.department} disabled />
          </div>
          <div className="field">
            <label htmlFor="pPosition">Position</label>
            <input id="pPosition" value={position} onChange={(e) => setPosition(e.target.value)} />
          </div>
        </div>

        <div className="form-row">
          <div className="field">
            <label htmlFor="pLevel">Employment Level</label>
            <select
              id="pLevel"
              value={level}
              onChange={(e) => {
                const next = e.target.value;
                if (isEmploymentLevel(next)) setLevel(next);
              }}
            >
              {EMP_LEVELS.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="pAccess">System Access (assigned by Main Access)</label>
            <input id="pAccess" value={ACCESS_LABEL[user.systemAccess]} disabled />
          </div>
        </div>

        <p style={{ fontSize: 11, color: 'var(--muted)' }}>
          Department and System Access are managed by Main Access via the Organization Chart and
          cannot be self-assigned.
        </p>

        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? <Spinner /> : null}
          {saving ? 'Saving…' : 'Save & Continue'}
        </button>
      </form>
    </div>
  );
}
